import {
  lazy,
  type PointerEvent as ReactPointerEvent,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Spinner, useToast } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { useCanvas } from "../../hooks/useCanvas";
import { getFileExtension, isPreviewable } from "../../lib/canvas";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  CopyIcon,
  DownloadIcon,
  WriteIcon,
} from "../icons";
import { CanvasCodeView } from "./CanvasCodeView";

// The preview machinery pulls in react-markdown + the iframe/mermaid renderers;
// load it only when the Canvas actually opens.
const CanvasPreview = lazy(() =>
  import("./CanvasPreview").then((m) => ({ default: m.CanvasPreview })),
);

/**
 * The Canvas: a right-side drawer (mirrors SourcesPanel) that previews / shows
 * the source of an artifact. Defaults to the Sources width and is drag-resizable
 * from its left edge; the conversation reflows beside it (never covered). The
 * source view is editable in place — Edit swaps in a textarea, Done writes the
 * draft back so the preview reflects the change.
 */
export function CanvasPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const canvas = useCanvas();
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [htmlNav, setHtmlNav] = useState({ canBack: false, canForward: false });
  const [hoverUrl, setHoverUrl] = useState<string | null>(null);

  const htmlFrameRef = useRef<HTMLIFrameElement | null>(null);

  const isOpen = canvas?.isOpen ?? false;
  const artifact = canvas?.artifact ?? null;
  const previewable = artifact ? isPreviewable(artifact.type) : false;
  const showPreview = previewable && canvas?.viewMode === "preview";
  // Editing is a source-view affordance — available whenever code (not preview) shows.
  const canEdit = !!artifact && !showPreview;
  // Back/forward only make sense for the live HTML preview iframe.
  const isHtmlPreview = artifact?.type === "html" && showPreview;

  function navHtml(dir: "back" | "forward") {
    htmlFrameRef.current?.contentWindow?.postMessage({ anyaiNav: dir }, "*");
  }

  useEffect(() => {
    if (!isOpen || !canvas) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") canvas?.close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, canvas]);

  // Drop any in-progress edit when the artifact changes or the panel closes.
  useEffect(() => {
    setIsEditing(false);
  }, [artifact?.id, isOpen]);

  // The HTML preview reports its in-page nav state so Back/Forward can disable.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== htmlFrameRef.current?.contentWindow) return;
      const data = event.data as {
        anyaiNavState?: unknown;
        canBack?: unknown;
        canForward?: unknown;
        anyaiHover?: unknown;
      };
      if (data?.anyaiNavState) {
        setHtmlNav({ canBack: Boolean(data.canBack), canForward: Boolean(data.canForward) });
      }
      if (typeof data?.anyaiHover === "string") {
        setHoverUrl(data.anyaiHover || null);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // A fresh artifact / reload starts with no history and nothing hovered.
  useEffect(() => {
    setHtmlNav({ canBack: false, canForward: false });
    setHoverUrl(null);
  }, [artifact?.id, showPreview]);

  function startEdit() {
    if (!artifact) return;
    setDraft(artifact.code);
    setIsEditing(true);
  }

  // Silent commit — used when switching to preview so the render reflects edits.
  function commitEdit() {
    if (artifact) canvas?.syncContent(artifact.id, draft);
    setIsEditing(false);
  }

  // Explicit "Done" — commit + confirm with a toast.
  function saveEdit() {
    commitEdit();
    toast(t("chat.canvas.saved"));
  }

  function switchView(mode: "preview" | "code") {
    if (isEditing) commitEdit();
    canvas?.setViewMode(mode);
  }

  async function copy() {
    if (!artifact) return;
    try {
      await navigator.clipboard.writeText(artifact.code);
      setCopied(true);
      toast(t("messages.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast(t("messages.couldntCopy"));
    }
  }

  function download() {
    if (!artifact) return;
    const blob = new Blob([artifact.code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `artifact.${getFileExtension(artifact.language)}`;
    a.click();
    URL.revokeObjectURL(url);
    toast(t("chat.canvas.downloaded"));
  }

  // Drag the left edge: the panel's right edge is pinned to the viewport, so the
  // width is simply the cursor's distance from the right edge.
  function startResize(event: ReactPointerEvent) {
    if (!canvas) return;
    event.preventDefault();
    document.documentElement.classList.add("canvas-resizing");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (e: PointerEvent) => canvas.setWidth(window.innerWidth - e.clientX);
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.documentElement.classList.remove("canvas-resizing");
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  const iconBtn =
    "grid size-8 place-items-center rounded-[8px] text-ink outline-none transition-colors duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:bg-line-soft [&_svg]:size-4";

  return createPortal(
    <aside
      aria-label={t("chat.canvas.title")}
      aria-hidden={!isOpen}
      style={{ width: canvas?.width ?? 380 }}
      className={cn(
        "fixed inset-y-0 right-0 z-40 flex max-w-[92vw] flex-col bg-panel-2 shadow-[var(--shadow-pill)] transition-transform duration-300 ease-house",
        isOpen ? "translate-x-0" : "pointer-events-none translate-x-full",
      )}
    >
      {/* Resize grip on the left edge (desktop only — mobile is an overlay). */}
      <button
        type="button"
        aria-label={t("chat.canvas.resize")}
        onPointerDown={startResize}
        className="absolute inset-y-0 left-0 z-10 hidden w-2.5 -translate-x-1/2 cursor-col-resize touch-none outline-none after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent after:transition-colors after:duration-150 hover:after:bg-line-strong focus-visible:after:bg-ink min-[881px]:block"
      />

      <header className="flex h-[60px] flex-none items-center gap-2 pl-5 pr-3">
        <span className="rounded-[6px] bg-line-soft px-2 py-0.5 font-sans text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
          {artifact?.language || t("chat.canvas.title")}
        </span>

        {previewable ? (
          <div className="flex items-center gap-0.5 rounded-[10px] bg-bubble p-0.5">
            {(["preview", "code"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => switchView(mode)}
                className={cn(
                  "rounded-[7px] px-2.5 py-1 font-sans text-[12px] font-medium outline-none transition-colors duration-150 ease-house",
                  canvas?.viewMode === mode
                    ? "bg-ink text-paper"
                    : "text-ink",
                )}
              >
                {mode === "preview" ? t("chat.canvas.preview") : t("chat.canvas.code")}
              </button>
            ))}
          </div>
        ) : null}

        {isHtmlPreview ? (
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navHtml("back")}
              disabled={!htmlNav.canBack}
              aria-label={t("chat.canvas.back")}
              title={t("chat.canvas.back")}
              className={cn(iconBtn, "disabled:pointer-events-none disabled:opacity-35")}
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              onClick={() => navHtml("forward")}
              disabled={!htmlNav.canForward}
              aria-label={t("chat.canvas.forward")}
              title={t("chat.canvas.forward")}
              className={cn(iconBtn, "disabled:pointer-events-none disabled:opacity-35")}
            >
              <ChevronRightIcon />
            </button>
          </div>
        ) : null}

        <span className="flex-1" />

        {canEdit ? (
          <button
            type="button"
            onClick={() => (isEditing ? saveEdit() : startEdit())}
            aria-pressed={isEditing}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-[8px] px-2 py-1 font-sans text-[12px] font-medium outline-none transition-colors duration-150 ease-house [&_svg]:size-3.5",
              isEditing
                ? "bg-ink text-paper"
                : "text-ink hover:bg-line-soft focus-visible:bg-line-soft",
            )}
          >
            {isEditing ? <CheckIcon /> : <WriteIcon />}
            {isEditing ? t("chat.canvas.done") : t("chat.canvas.edit")}
          </button>
        ) : null}

        <button type="button" onClick={copy} aria-label={t("messages.copy")} className={iconBtn}>
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
        <button
          type="button"
          onClick={download}
          aria-label={t("chat.canvas.download")}
          className={iconBtn}
        >
          <DownloadIcon />
        </button>
        <button
          type="button"
          onClick={() => canvas?.close()}
          aria-label={t("common.close")}
          className={iconBtn}
        >
          <CloseIcon />
        </button>
      </header>

      <div className="relative min-h-0 flex-1">
        {isHtmlPreview && hoverUrl ? (
          <div className="pointer-events-none absolute bottom-2 left-2 z-10 max-w-[85%] truncate rounded-md bg-paper/95 px-2 py-1 font-mono text-[11px] text-ink-soft shadow-[var(--shadow-soft)]">
            {hoverUrl}
          </div>
        ) : null}
        {artifact ? (
          showPreview ? (
            <Suspense
              fallback={
                <div className="grid h-full place-items-center">
                  <Spinner label={t("chat.canvas.title")} />
                </div>
              }
            >
              <CanvasPreview code={artifact.code} type={artifact.type} frameRef={htmlFrameRef} />
            </Suspense>
          ) : isEditing ? (
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              spellCheck={false}
              aria-label={t("chat.canvas.edit")}
              className="h-full w-full resize-none bg-paper p-4 font-mono text-[13px] leading-[1.6] text-ink outline-none"
            />
          ) : (
            <CanvasCodeView code={artifact.code} language={artifact.language} />
          )
        ) : null}
      </div>
    </aside>,
    document.body,
  );
}
